<template>
  <div class="flex flex-grow flex-col">
    <nav class="flex-1 space-y-1" aria-label="Drawer">
      <template v-for="item in items" :key="item.name">
        <div v-if="!item.children">
          <NuxtLink
            :to="item.href"
            @click="() => item.href && $emit('changeNav', item.href!)"
            :exact-active-class="activeClass"
            class="hover:bg-neutral-100 dark:hover:!bg-neutral-800 text-neutral-500 hover:text-neutral-700 dark:hover:!text-neutral-300 group w-full flex items-center pl-3 py-2 font-medium rounded-md"
          >
            <component
              v-if="item.icon"
              :is="item.icon"
              class="mr-3 flex-shrink-0"
              aria-hidden="true"
            />
            <span>{{ item.name }}</span>
          </NuxtLink>
        </div>
        <HeadlessDisclosure
          as="div"
          v-else
          class="space-y-1"
          :class="{ [headerClass]: item.alwaysOpen }"
          :default-open="item.defaultOpen || item.alwaysOpen"
          v-slot="{ open }"
        >
          <component
            :is="item.alwaysOpen ? 'div' : HeadlessDisclosureButton"
            :class="{
              'text-neutral-700 dark:text-neutral-300': open,
              'text-neutral-500': !open,
              'hover:bg-neutral-100 hover:text-neutral-700 dark:hover:bg-neutral-800 dark:hover:text-neutral-300 group w-full flex items-center pl-3 pr-1 py-2 text-left font-medium rounded-md focus:outline-none':
                !item.alwaysOpen,
              'pointer-events-none font-bold pl-2': item.alwaysOpen,
            }"
          >
            <component
              v-if="item.icon"
              :is="item.icon"
              class="mr-3 flex-shrink-0"
              aria-hidden="true"
            />
            <span class="flex-1">{{ item.name }}</span>
            <svg
              v-if="!item.alwaysOpen"
              class="'ml-3 h-5 w-5 flex-shrink-0 transform transition-colors duration-150 ease-in-out group-hover:text-neutral-700'"
              :class="{
                'text-neutral-700 dark:text-neutral-300 rotate-90': open,
                'text-neutral-500': !open,
              }"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path d="M6 6L14 10L6 14V6Z" fill="currentColor" />
            </svg>
          </component>
          <HeadlessDisclosurePanel class="space-y-1">
            <component
              v-for="subItem in item.children"
              :key="subItem.name"
              :is="subItem.href || subItem.to ? NuxtLink : 'div'"
              class="hover:bg-neutral-100 dark:hover:!bg-neutral-800 text-neutral-500 hover:text-neutral-700 dark:hover:!text-neutral-300 group w-full flex items-center p-2 text-left font-medium rounded-md focus:outline-none cursor-pointer"
              :to="subItem.href"
              @click.stop="
                (e: MouseEvent) => {
                  if (subItem.href) emit('changeNav', subItem.href ?? '/');
                  if (subItem.action) subItem.action(e);
                }
              "
              :exact-active-class="exactActiveClass"
              :active-class="activeClass"
              :class="{ [offsetClass]: offsetClass }"
            >
              {{ subItem.name }}
            </component>
          </HeadlessDisclosurePanel>
        </HeadlessDisclosure>
      </template>
    </nav>
  </div>
</template>

<script setup lang="ts">
import type { NestedNavigationItem } from "~/types/navigation";
import { HeadlessDisclosureButton, NuxtLink } from "#components";

export type NavItem = {
  current?: boolean;
  alwaysOpen?: boolean;
} & NestedNavigationItem;

export interface Props {
  items: NavItem[];
  right?: boolean;
  activeClass?: string;
  exactActiveClass?: string;
  offsetClass?: string;
  headerClass?: string;
}

interface Events {
  (e: "changeNav", v: string): void;
}

withDefaults(defineProps<Props>(), {
  activeClass:
    "bg-neutral-200 text-neutral-900 dark:bg-neutral-800 dark:text-neutral-100",
  offsetClass: "pl-12",
  headerClass: "[&:not(:first-child)]:pt-4",
});
const emit = defineEmits<Events>();
</script>
